import classnames from 'classnames';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import Highcharts, {
  AxisSetExtremesEventObject,
  Options as HighchartsOptions,
  Point,
  SeriesGanttOptions,
  XAxisCurrentDateIndicatorLabelOptions,
  XAxisCurrentDateIndicatorOptions,
  XAxisOptions,
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsGantt from 'highcharts/modules/gantt';
import cloneDeep from 'lodash.clonedeep';
import { clone, isEmpty } from 'ramda';
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';

import { useAuth } from '../../app/AuthorizationProvider';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import { STATIC_ELEMENT_HEIGHT, STATIC_POINT_HEIGHT, TOOLTIP_PADDING_SIZE } from '../../constants';
import { getZonedDateTimezoneOffset } from '../../utils/date';
import { identifyTimeFormat, TIME_FORMAT_12_HOURS } from '../../utils/time';
import { ganttChartSeriesConfig, TOOLTIP_X_AXIS_THRESHOLD } from '../AdherenceHistory/config';
import { formatScheduleElementLabel, ganttChartTooltipFormatter } from '../AdherenceHistory/utils';
import { MIN_15, MIN_5, ONE_HOUR, MIN_1, MIN_15_DROP_ELEMENT_WIDTH } from '../Scheduling/config';
import { differenceInValues } from '../Scheduling/utils/mapChartData';
import { CURRENT_TIME_INDICATOR_MARGIN, ganttChartOptions } from './config';
import { GanttChartProps } from './types';

highchartsGantt(Highcharts);

export const CHART_15_MIN_INTERVAL_WIDTH = 40;
const CHART_1_MIN_INTERVAL_WIDTH = CHART_15_MIN_INTERVAL_WIDTH / 15;
const THRESHOLD_FOR_MOVING_TOOLTIP_ON_X_AXIS = 10000; // value in milliseconds - how often should be the position of the tooltip recalculated for ongoing events whose end time continuously changes

type ChartState = {
  chartWidth: number;
  isZoomed: boolean;
  xAxisHoursTickInterval: number;
  xAxisMinutesTickInterval: number;
};

export const GanttChartComponent: React.FC<GanttChartProps> = ({
  activityElements,
  agentId,
  endTime,
  isDataEmpty,
  isLoading,
  outOfAdherenceElements,
  scheduleElements,
  startTime,
  timezone,
  timeStamp,
}) => {
  const { locale, localeObj } = useAuth();

  const is12HoursTimeFormat = identifyTimeFormat(locale) === TIME_FORMAT_12_HOURS;

  const scrollbarRef = useRef<PerfectScrollbar>(null);
  const chartRef = useRef<HighchartsReact>(null);

  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [scrollbarContainerWidth, setScrollbarContainerWidth] = useState(0);

  const timelineHoursCount = differenceInHours(endTime, startTime);
  const timelineWidth = timelineHoursCount * 4 * CHART_15_MIN_INTERVAL_WIDTH;
  const containerWidth = containerRef?.getBoundingClientRect().width;
  const containerWidthRounded = containerWidth ? Math.round(containerWidth) : undefined;
  const defaultChartWidth = Math.max(timelineWidth, containerWidthRounded ?? scrollbarContainerWidth - 50);

  const shouldSkipChartRendering = !(
    activityElements.length ||
    outOfAdherenceElements.length ||
    scheduleElements?.length
  );

  const [chartState, setChartState] = useState<ChartState>({
    chartWidth: defaultChartWidth,
    isZoomed: false,
    xAxisHoursTickInterval: ONE_HOUR,
    xAxisMinutesTickInterval: MIN_15,
  });

  const { t } = useTranslation();

  useEffect(() => {
    chartRef.current?.chart.zoomOut();
  }, [agentId]);

  useEffect(() => {
    // updating x-axis to refresh timestamp within currentDateIndicator
    chartRef.current?.chart?.xAxis?.[0]?.update({});
  }, [timeStamp]);

  const scrollToCenter = useCallback(() => {
    if (containerWidthRounded && containerRef) {
      scrollbarRef?.current?.forceUpdate();
      const currentQuarterHour = Math.floor(new Date(timeStamp).getMinutes() / 15);

      const containerWidthIncludingYAxis =
        containerRef.parentElement?.getBoundingClientRect().width ?? containerWidthRounded;

      const yAxisWidth = containerWidthIncludingYAxis - containerWidthRounded;

      containerRef.scrollLeft =
        Math.round(chartState.chartWidth / 2 - containerWidthRounded / 2) +
        yAxisWidth / 2 +
        currentQuarterHour * MIN_15_DROP_ELEMENT_WIDTH;
    }
  }, [chartState.chartWidth, containerRef, containerWidthRounded, timeStamp]);

  useEffect(() => {
    if (!shouldSkipChartRendering && containerWidthRounded && containerRef && scrollbarContainerWidth) {
      scrollToCenter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, chartState.chartWidth, containerWidthRounded, shouldSkipChartRendering, startTime]);

  useLayoutEffect(() => {
    scrollbarRef?.current?.forceUpdate();

    // needs to be delayed to not hit too early
    const timeOut = setTimeout(() => {
      scrollToCenter();
    }, 50);

    return () => {
      clearTimeout(timeOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartState.isZoomed]);

  const options: HighchartsOptions = useMemo(() => {
    const data = {
      outOfAdherence: outOfAdherenceElements,
      reality: activityElements,
      scheduled: scheduleElements,
    };

    return {
      ...ganttChartOptions,
      chart: {
        ...ganttChartOptions.chart,
        width: chartState.chartWidth,
      },
      series: [
        {
          data: [
            {
              start: startTime,
              end: endTime,
              y: 0,
              color: '#f7f8f9',
            },
          ],
          enableMouseTracking: false,
          id: 'out-of-adherence-plot-band',
          pointWidth: STATIC_POINT_HEIGHT + 4,
          type: 'gantt',
          xAxis: 0,
        },
        ...ganttChartSeriesConfig.map(({ id, nameKey }) => ({
          id,
          cropThreshold: 1,
          data: clone(data[id]),
          dataLabels:
            id === 'outOfAdherence'
              ? {
                  enabled: false,
                }
              : {
                  defer: false,
                  enabled: true,
                  formatter: formatScheduleElementLabel(id === 'scheduled'),
                  style: {
                    fontSize: '12px',
                  },
                },
          name: t(nameKey),
          type: 'gantt' as const,
          xAxis: 0,
        })),
      ],
      time: {
        getTimezoneOffset: (timestamp: number) =>
          getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
        useUTC: true,
      },
      tooltip: {
        ...ganttChartOptions.tooltip,
        formatter: ganttChartTooltipFormatter(t, timezone, 'real-time-adherence'),
        positioner(labelWidth, labelHeight) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const { chart } = this; // `this` is of type `TooltipOptions`
          const {
            hoverSeries: {
              xAxis,
              userOptions: { id },
            },
            pointer: {
              chartPosition: { left, top },
            },
          } = chart;

          const { hoverPoint } = chart;
          const yOffset = id === 'outOfAdherence' ? xAxis.top + 1 - CURRENT_TIME_INDICATOR_MARGIN : 0;

          const y = top + xAxis.top + hoverPoint.plotY - hoverPoint.pointWidth - labelHeight - yOffset;

          const { min: minX, max: maxX } = xAxis;

          const currentChart1MinuteIntervalWidth =
            chartState.chartWidth < defaultChartWidth
              ? chartState.chartWidth / ((maxX - minX) / MIN_1)
              : CHART_1_MIN_INTERVAL_WIDTH;

          const scrollOffset = containerRef ? Math.round(containerRef.scrollLeft) : 0;
          const endOfPoint = Math.min(hoverPoint.end - (hoverPoint.end % THRESHOLD_FOR_MOVING_TOOLTIP_ON_X_AXIS), maxX);
          const endOfPointXAxisCoordinate = ((endOfPoint - minX) / MIN_1) * currentChart1MinuteIntervalWidth;
          const maxVisibleXPosition = scrollOffset + (containerWidthRounded ?? 0);
          const startOfPoint = Math.max(hoverPoint.start, minX);
          const startOfPointXAxisCoordinate = ((startOfPoint - minX) / MIN_1) * currentChart1MinuteIntervalWidth;

          const startXPosition = Math.max(startOfPointXAxisCoordinate, scrollOffset);
          const endXPosition = Math.min(endOfPointXAxisCoordinate, maxVisibleXPosition);

          let xPosition = Math.round(
            left +
              scrollOffset +
              Math.max(startXPosition - scrollOffset, 0) +
              (endXPosition - startXPosition) / 2 -
              labelWidth / 2 -
              2 * TOOLTIP_X_AXIS_THRESHOLD
          );

          if (xPosition + labelWidth + TOOLTIP_PADDING_SIZE > window.innerWidth) {
            // prevent overflow out of the body content from right side
            xPosition = window.innerWidth - labelWidth - TOOLTIP_PADDING_SIZE;
          }

          return {
            y,
            x: Math.max(xPosition, 0),
          };
        },
      },
      xAxis: [
        {
          ...(ganttChartOptions.xAxis as XAxisOptions[])[0],
          currentDateIndicator: {
            ...((ganttChartOptions.xAxis as XAxisOptions[])[0]
              .currentDateIndicator as XAxisCurrentDateIndicatorOptions),
            label: {
              ...(
                (ganttChartOptions.xAxis as XAxisOptions[])[0].currentDateIndicator as XAxisCurrentDateIndicatorOptions
              ).label,
              formatter: () =>
                format(utcToZonedTime(new Date(), timezone), t('date.formats.timeWithSecondsShort'), {
                  locale: localeObj,
                  timeZone: timezone,
                }),
              className: classnames('real-time-adherence__gantt-chart__current-time-indicator-label', {
                'real-time-adherence__gantt-chart__current-time-indicator-label--12': is12HoursTimeFormat,
              }),
            } as XAxisCurrentDateIndicatorLabelOptions,
          },
          events: {
            setExtremes(event: AxisSetExtremesEventObject) {
              /**
               * should take care of zooming-out functionality only, because for some reason,
               * zooming-in on this layer is causing performance issues
               */

              if (!containerRef || !event.trigger || !chartRef?.current) {
                return event;
              }

              const { min: currentMin, max: currentMax } = event;
              const isZoomed = (currentMin || currentMax) && (startTime !== currentMin || endTime !== currentMax);

              if (isZoomed) {
                // when zoomed-in, update of axes should be in responsibility of afterSetExtremes handler
                return event;
              }

              setChartState({
                isZoomed: false,
                xAxisMinutesTickInterval: MIN_15,
                xAxisHoursTickInterval: ONE_HOUR,
                chartWidth: defaultChartWidth,
              });

              return event;
            },
            afterSetExtremes(event: AxisSetExtremesEventObject) {
              /**
               * should take care of zooming-in functionality only, because for some reason,
               * zooming-out on this layer is causing performance issues
               */

              if (!containerRef || !event.trigger || !chartRef?.current) {
                return;
              }

              const { min: currentMin, max: currentMax } = event;
              const isZoomed = (currentMin || currentMax) && (startTime !== currentMin || endTime !== currentMax);

              if (!isZoomed) {
                // when zoomed-out, update of axes should be in responsibility of setExtremes handler
                return;
              }

              let tickInterval: number;
              const zoomedRangeInMinutes = differenceInMinutes(currentMax, currentMin);

              if (zoomedRangeInMinutes <= 15) {
                // use 1 minute tick interval when zoom range is 15 minutes or less
                tickInterval = MIN_1;
              } else if (zoomedRangeInMinutes <= 60) {
                // use 5 minutes tick interval when zoom range is between 15 minutes and 1 hour
                tickInterval = MIN_5;
              } else {
                // use 15 minutes tick interval by default
                tickInterval = MIN_15;
              }

              const outerContainerWidth = containerRef.getBoundingClientRect().width;
              const newWidth = outerContainerWidth ? Math.round(outerContainerWidth) : null;

              setChartState((state) => ({
                isZoomed: true,
                xAxisMinutesTickInterval: tickInterval,
                xAxisHoursTickInterval: zoomedRangeInMinutes < 60 ? MIN_15 : state.xAxisHoursTickInterval,
                chartWidth: newWidth ?? state.chartWidth,
              }));
            },
          },
          labels: {
            ...(ganttChartOptions.xAxis as XAxisOptions[])[0].labels,
            formatter() {
              // eslint-disable-next-line react/no-this-in-sfc
              const dateTime = this.value;
              const zonedDateTime = utcToZonedTime(dateTime, timezone);
              const hours = format(zonedDateTime, 'HH', { locale: localeObj, timeZone: timezone });
              const minutes = format(zonedDateTime, 'mm', { locale: localeObj, timeZone: timezone });

              return `<span class="schedule-time">${hours}:${minutes}</span>:${minutes}`;
            },
          },
          max: endTime,
          min: startTime,
          tickInterval: chartState.xAxisMinutesTickInterval,
        },
        {
          ...(ganttChartOptions.xAxis as XAxisOptions[])[1],
          labels: {
            ...(ganttChartOptions.xAxis as XAxisOptions[])[1].labels,
            formatter() {
              // eslint-disable-next-line react/no-this-in-sfc
              const dateTime = this.value;

              return format(utcToZonedTime(dateTime, timezone), t('date.formats.timeShort'), {
                locale: localeObj,
                timeZone: timezone,
              });
            },
          },
          max: endTime,
          min: startTime,
          tickInterval: chartState.xAxisHoursTickInterval,
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activityElements,
    defaultChartWidth,
    chartState.chartWidth,
    chartState.xAxisHoursTickInterval,
    chartState.xAxisMinutesTickInterval,
    endTime,
    outOfAdherenceElements,
    scheduleElements,
    startTime,
    timezone,
  ]);

  useEffect(() => {
    if (chartRef?.current?.chart) {
      const { chart } = chartRef.current;

      const xAxis = chart.xAxis[0];
      if (xAxis) {
        /**
         * Issue https://github.com/highcharts/highcharts/issues/6943
         * To prevent new setExtremes() and afterSetExtremes() handlers from being added to existing ones,
         * when using native Highcharts' update function (they should have been replaced instead).
         * final fix will be to upgrade Highcharts to the newest version once defined in issue 6943
         */
        if (chartState.isZoomed) {
          Highcharts.removeEvent(xAxis, 'afterSetExtremes');
        } else {
          Highcharts.removeEvent(xAxis, 'setExtremes');
        }
      }

      // Do not compare series data at all, as they are manually set on every render.
      const clonedOptions = cloneDeep(options) as HighchartsOptions;
      // eslint-disable-next-line no-param-reassign
      (clonedOptions?.series as SeriesGanttOptions[])?.forEach((series) => delete series.data);

      const toUpdate = differenceInValues(chart.options, clonedOptions);

      // update series data manually!
      options.series?.forEach((series, index) => {
        // @ts-expect-error: incompatible type 😥
        chart.series[index].setData(series.data, false);
      });

      // @ts-expect-error: incompatible type 😥
      const hoveredPointId = chart.hoverPoint?.id;
      const { hoverSeries } = chart;

      if (toUpdate && !isEmpty(toUpdate)) {
        // disable animation on update
        chart.update(cloneDeep(toUpdate), false, true, false);
      }

      chart.redraw(false);

      // restore hovered point & series after redraw so that user could interact with previously hovered point
      if (hoveredPointId && hoverSeries) {
        chart.hoverPoint = chart.get(hoveredPointId) as Point;
        chart.hoverSeries = hoverSeries;
        chart.tooltip.refresh(chart.get(hoveredPointId) as Point);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, chartRef]);

  useEffect(() => {
    const chart = chartRef.current?.chart;

    if (!chart) {
      return () => null;
    }

    const top = chart.plotTop;

    // set background color for xAxis minutes section (adding new rectangle underneath the axis elements)
    const minutesAxisBackground = chart.renderer.rect(
      0,
      top - STATIC_ELEMENT_HEIGHT,
      chartState.chartWidth,
      STATIC_ELEMENT_HEIGHT - 2
    );

    minutesAxisBackground.add().attr({ fill: '#f7f8f9' });

    // set background color for xAxis hours section (adding new rectangle underneath the axis elements)
    const hoursAxisBackground = chart.renderer.rect(
      0,
      top - 2 * STATIC_ELEMENT_HEIGHT - 8,
      chartState.chartWidth,
      STATIC_ELEMENT_HEIGHT + 6
    );

    hoursAxisBackground.add().attr({ fill: '#d4dee3' });

    return () => {
      minutesAxisBackground.destroy();
      hoursAxisBackground.destroy();
    };
  }, [chartState.chartWidth, containerWidthRounded, shouldSkipChartRendering]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateChartSize = () => {
    chartRef?.current?.chart.zoomOut();
    scrollbarRef.current?.forceUpdate();
    chartRef?.current?.chart.reflow();
  };

  const onResizeHandler = (width?: number) => {
    if (!width) {
      return;
    }

    setScrollbarContainerWidth(Math.round(width));
    chartRef?.current?.chart.reflow();
    updateChartSize();
  };

  return (
    <ReactResizeDetector handleWidth onResize={onResizeHandler} refreshMode="debounce" refreshRate={10}>
      <PerfectScrollbar
        className="real-time-adherence__gantt-chart-scrollbar-container"
        containerRef={setContainerRef}
        ref={scrollbarRef}
        options={{
          scrollingThreshold: 100,
          swipeEasing: true,
          scrollXMarginOffset: 40, // need to be bigger than half width of the currentTime indicator
        }}
      >
        <div data-test-id="real-time-adherence-detail:gantt-chart">
          {isLoading && shouldSkipChartRendering ? null : (
            <>
              {isDataEmpty ? (
                <FullSizeMessage headline={t('filter.noDataAvailable')} iconKind="no_data" />
              ) : (
                <HighchartsReact
                  ref={chartRef}
                  highcharts={Highcharts}
                  options={!chartRef?.current?.chart ? options : {}}
                  constructorType="ganttChart"
                />
              )}
            </>
          )}
        </div>
      </PerfectScrollbar>
    </ReactResizeDetector>
  );
};

export const GanttChart = memo(GanttChartComponent);
